/**
 * Pure ordering + planner logic (no Firebase) so tests can pin it down.
 *
 * Lineup (spec page 5): Impact x Urgency sets the default order; a drag sets a
 * manual rank. Display order: requests that have a rank keep their rank order;
 * requests without one (new to the lineup) slot in by score - before the first
 * ranked request with a lower score that wasn't pinned by hand. "Re-sort by score" rewrites every rank
 * from score and clears the manual flag.
 *
 * Planner (spec page 7): slots today / tomorrow / week, each stamped with the
 * date it was planned for. Unfinished Today items stay in Today ("rolled");
 * Tomorrow items become Today once that date arrives. No background job.
 */
import { score } from '../config';

export interface Rankable {
  id: string;
  rank?: number;
  rankManual?: boolean;
  impact?: number;
  urgency?: number;
  createdAt?: { toMillis(): number } | null;
}

const sc = (r: Rankable) => score(r.impact, r.urgency) ?? -1;
const created = (r: Rankable) => r.createdAt?.toMillis() ?? 0;
const byScore = (a: Rankable, b: Rankable) => sc(b) - sc(a) || created(a) - created(b);

export function lineupOrder<T extends Rankable>(items: T[]): T[] {
  const ranked = items.filter((r) => typeof r.rank === 'number').sort((a, b) => a.rank! - b.rank!);
  const unranked = items.filter((r) => typeof r.rank !== 'number').sort(byScore);
  const out: T[] = [...ranked];
  for (const u of unranked) {
    // Never jump above something Baker/Robin pinned by hand.
    const i = out.findIndex((x) => !x.rankManual && sc(x) < sc(u));
    if (i < 0) out.push(u); else out.splice(i, 0, u);
  }
  return out;
}

export const RANK_STEP = 1000;

/** New ranks after moving `movedId` to position `to` in `ordered`. Only changed rows are returned. */
export function reorder<T extends Rankable>(ordered: T[], movedId: string, to: number) {
  const from = ordered.findIndex((r) => r.id === movedId);
  if (from < 0 || from === to) return [];
  const next = [...ordered];
  const [m] = next.splice(from, 1);
  next.splice(to, 0, m);
  return next
    .map((r, i) => ({ r, rank: (i + 1) * RANK_STEP, manual: r.id === movedId ? true : !!r.rankManual }))
    .filter(({ r, rank, manual }) => r.rank !== rank || !!r.rankManual !== manual);
}

/** "Re-sort by score": ranks from score, manual pins cleared. */
export function resortByScore<T extends Rankable>(items: T[]) {
  return [...items].sort(byScore)
    .map((r, i) => ({ r, rank: (i + 1) * RANK_STEP, manual: false }))
    .filter(({ r, rank, manual }) => r.rank !== rank || !!r.rankManual !== manual);
}

// ---- planner --------------------------------------------------------------

export type Slot = 'none' | 'today' | 'tomorrow' | 'week';

export function isoDate(d = new Date()): string {
  const y = d.getFullYear(), m = String(d.getMonth() + 1).padStart(2, '0'), day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}
export function addDays(iso: string, n: number): string {
  const [y, m, d] = iso.split('-').map(Number);
  return isoDate(new Date(y, m - 1, d + n));
}

/** The date a slot should be stamped with when an item is dropped into it. */
export function plannedDateFor(slot: Slot, today = isoDate()): string | undefined {
  if (slot === 'today' || slot === 'week') return today;
  if (slot === 'tomorrow') return addDays(today, 1);
  return undefined;
}

/** Where an item shows today, and whether it rolled over from an earlier day. */
export function effectiveSlot(slot: Slot | undefined, plannerDate: string | undefined, today = isoDate()):
  { slot: Slot; rolled: boolean } {
  const s = slot ?? 'none';
  if (s === 'tomorrow' && plannerDate && plannerDate <= today) return { slot: 'today', rolled: plannerDate < today };
  if (s === 'today' && plannerDate && plannerDate < today) return { slot: 'today', rolled: true };
  return { slot: s, rolled: false };
}
